import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase } from './creative-api';

/**
 * A banner's casino-creation definition: what the banner shows, as the casino's catalog describes it (the way
 * Shuffle Studio's Art Manager builds its previews). A new version can be drawn from it ("From its definition")
 * instead of from the current artwork. Read-only here; spark-creatives resolves it for the brand's style.
 */

/** One of the definition's own reference images (drawn into the banner before the owner's own). */
export interface BannerDefinitionReference {
  url: string;
  name: string;
}

export interface BannerDefinition {
  /** The banner has a definition a version can be drawn from. False for any other banner. */
  available: boolean;
  /** Where it comes from (`evergreen` today); '' when not available. */
  source: string;
  /** The catalog name (e.g. "Drops&Wins"). */
  name: string;
  /** Which entry was used: the style's own, or `default`. */
  entry: string;
  /** What the banner shows (the SUBJECT the draw starts from). */
  subject: string;
  /** The definition's other fields, as `Label: value` lines. */
  lines: string[];
  references: BannerDefinitionReference[];
  /** What to take from the references; '' when the definition does not say. */
  referencesBehavior: string;
}

export const NO_BANNER_DEFINITION: BannerDefinition = {
  available: false,
  source: '',
  name: '',
  entry: '',
  subject: '',
  lines: [],
  references: [],
  referencesBehavior: '',
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The service's answer as the composer uses it; anything malformed (or without a subject) reads as no definition. */
export const parseBannerDefinition = (raw: unknown): BannerDefinition => {
  if (!raw || typeof raw !== 'object') return NO_BANNER_DEFINITION;
  const r = raw as Record<string, unknown>;
  const subject = str(r.subject).trim();
  if (r.available !== true || subject === '') return NO_BANNER_DEFINITION;
  const lines = (Array.isArray(r.lines) ? r.lines : []).map(str).filter((line) => line.trim() !== '');
  const references: BannerDefinitionReference[] = [];
  for (const item of Array.isArray(r.references) ? r.references : []) {
    const ref = (item ?? {}) as Record<string, unknown>;
    const url = str(ref.url);
    if (url) references.push({ url, name: str(ref.name) });
  }
  return {
    available: true,
    source: str(r.source),
    name: str(r.name),
    entry: str(r.entry),
    subject,
    lines,
    references,
    referencesBehavior: str(r.references_behavior),
  };
};

/** The banner's definition; null when it could not be read (the composer then offers today's edit only). */
export const fetchBannerDefinition = async (
  tenantId: number,
  bannerId: string,
  signal?: AbortSignal,
): Promise<BannerDefinition | null> => {
  if (!creativesServiceBase()) return null;
  try {
    const res = await axios.get(
      `${creativesServiceBase()}/creatives/tenant/${encodeURIComponent(String(tenantId))}/banner/${encodeURIComponent(bannerId)}/definition`,
      creativesRequestConfig(signal),
    );
    return res.status === 200 ? parseBannerDefinition(res.data) : null;
  } catch {
    return null;
  }
};
